package music.jammers.client.shared.profile.presentation.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.common_close_button
import limasoul.client.shared.generated.resources.error_dialog_title
import limasoul.client.shared.generated.resources.ic_admin_settings
import limasoul.client.shared.generated.resources.ic_open_new
import limasoul.client.shared.generated.resources.profile_admin_settings_button
import limasoul.client.shared.generated.resources.profile_city_placeholder
import limasoul.client.shared.generated.resources.profile_edit
import limasoul.client.shared.generated.resources.profile_logout_button
import limasoul.client.shared.generated.resources.profile_name_placeholder
import limasoul.client.shared.generated.resources.profile_save_button
import limasoul.client.shared.generated.resources.profile_screen_title
import limasoul.client.shared.generated.resources.profile_telegram_placeholder
import music.jammers.client.shared.component.AppTextField
import music.jammers.client.shared.component.BackgroundBox
import music.jammers.client.shared.component.ErrorDialog
import music.jammers.client.shared.component.FieldValidationState
import music.jammers.client.shared.component.JammersTopAppBar
import music.jammers.client.shared.component.Progress
import music.jammers.client.shared.event.presentation.ui.getString
import music.jammers.client.shared.profile.di.ProfileComponent
import music.jammers.client.shared.profile.domain.ValidateProfileUseCase
import music.jammers.client.shared.profile.presentation.viewmodel.ProfilePageState
import music.jammers.client.shared.profile.presentation.viewmodel.ProfileViewModel
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

const val PROFILE_SCREEN = "ProfileScreen"

@Composable
fun ProfileScreen(
    userId: Long?,
) {
    val viewModel = remember { ProfileComponent.profileViewModel.apply { onBind(userId) } }
    val viewModelState = viewModel.state.value
    val editModeState = viewModel.editModeState.value

    val validationState = viewModel.validationState.value

    Scaffold(
        topBar = {
            JammersTopAppBar(
                text = stringResource(Res.string.profile_screen_title),
                navigationIcon = {
                    if (editModeState) {
                        IconButton(
                            onClick = viewModel::onEditModeClosed,
                        ) {
                            Icon(
                                Icons.Default.Close,
                                contentDescription = stringResource(Res.string.common_close_button)
                            )
                        }
                    } else {
                        IconButton(
                            onClick = viewModel::onCloseButtonClicked,
                        ) {
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = "Back",
                            )
                        }
                    }
                },
                actions = {
                    if (viewModelState.editAvailable) {
                        if (editModeState) {
                            IconButton(
                                onClick = viewModel::onSaveProfileClicked,
                                enabled = !viewModel.state.value.profileSavingInProgress,
                            ) {
                                Icon(
                                    Icons.Default.Check,
                                    contentDescription = stringResource(Res.string.profile_save_button)
                                )
                            }
                        } else {
                            IconButton(
                                onClick = viewModel::onEditModeClicked,
                            ) {
                                Icon(
                                    Icons.Default.Edit,
                                    contentDescription = stringResource(Res.string.profile_edit)
                                )
                            }
                        }

                        if (viewModel.state.value.needShowAdminSettingsButton) {
                            Spacer(modifier = Modifier.size(4.dp))

                            IconButton(
                                onClick = viewModel::onAdminSettingsClicked,
                            ) {
                                Icon(
                                    painterResource(Res.drawable.ic_admin_settings),
                                    contentDescription = stringResource(Res.string.profile_admin_settings_button),
                                )
                            }
                        }
                    }
                },
            )
        },
    ) { padding ->
        BackgroundBox {
            if (viewModelState.pageDataLoading) {
                Progress()
            } else {
                Content(
                    validationState = validationState,
                    viewModel = viewModel,
                    viewState = viewModelState,
                    editMode = editModeState,
                    padding = padding,
                )
            }
        }
    }
}

@Composable
private fun Content(
    validationState: FieldValidationState,
    viewModel: ProfileViewModel,
    viewState: ProfilePageState,
    editMode: Boolean,
    padding: PaddingValues,
) {
    Column(
        modifier = Modifier.padding(padding).padding(vertical = 16.dp, horizontal = 32.dp)
    ) {
        AsyncImage(
            model = viewState.picture,
            contentDescription = "user_picture",
            contentScale = ContentScale.Crop,
            modifier = Modifier.size(160.dp)
                .align(Alignment.CenterHorizontally)
                .clip(shape = CircleShape),
        )

        Spacer(modifier = Modifier.size(16.dp))

        val nameError = validationState.getValidationError(ValidateProfileUseCase.NAME).getString()
        AppTextField(
            value = viewState.userName,
            placeholder = { Text(stringResource(Res.string.profile_name_placeholder)) },
            onValueChange = { newText -> viewModel.onNameChanged(newText) },
            enabled = editMode && !viewState.profileSavingInProgress,
            errorText = nameError,
            modifier = Modifier.fillMaxWidth(),
        )

        val telegramError = validationState.getValidationError(ValidateProfileUseCase.TELEGRAM).getString()
        Box {
            AppTextField(
                placeholder = { Text(stringResource(Res.string.profile_telegram_placeholder)) },
                value = viewState.telegram,
                onValueChange = { newText -> viewModel.onTelegramChanged(newText) },
                enabled = editMode && !viewState.profileSavingInProgress,
                errorText = telegramError,
                modifier = Modifier.fillMaxWidth(),
            )

            if (viewState.telegramOpenVisible) {
                Icon(
                    painter = painterResource(Res.drawable.ic_open_new),
                    contentDescription = "Open telegram",
                    modifier = Modifier.align(Alignment.CenterEnd)
                        .clickable {
                            viewModel.onTelegramOpen()
                        },
                )
            }
        }

        val cityError = validationState.getValidationError(ValidateProfileUseCase.CITY).getString()
        AppTextField(
            placeholder = { Text(stringResource(Res.string.profile_city_placeholder)) },
            value = viewState.city,
            onValueChange = { newText -> viewModel.onCityChanged(newText) },
            enabled = editMode && !viewState.profileSavingInProgress,
            errorText = cityError,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.size(4.dp))

        if (viewState.editAvailable) {
            OutlinedButton(
                onClick = viewModel::onLogOutClicked,
                modifier = Modifier.align(Alignment.CenterHorizontally)
            ) {
                Text(
                    text = stringResource(Res.string.profile_logout_button).uppercase(),
                    style = MaterialTheme.typography.headlineSmall,
                    modifier = Modifier.padding(horizontal = 64.dp),
                )
            }
        }

        Spacer(modifier = Modifier.size(8.dp))

        if (viewState.profileSavingInProgress) {
            Progress(verticalArrangement = Arrangement.Top)
        }

        viewState.error?.let { throwable ->
            ErrorDialog(
                throwable.message ?: stringResource(Res.string.error_dialog_title)
            ) {
                viewModel.onErrorClosed()
            }
        }

        viewState.openUrl?.let { url ->
            LocalUriHandler.current.openUri(url)
            viewModel.onUrlOpened()
        }
    }
}